import { FormControl, FormControlLabel, Radio, RadioGroup, TextField, MenuItem, InputLabel, Select, Button } from '@mui/material'
import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next';
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import { cipherSuiteOptions, destinationTypeOptions } from '../../../constants/constantList';
import { debounce } from 'lodash';
import _ from 'lodash';
import { stringifyJSONAttributes, stringToBooleanConversion, encryptString, decryptString } from '../../../utils/utilFunctions';
import SmartPasswordField from '../../../commonComponents/SmartPasswordField';
import { MoreHorizOutlined } from '@mui/icons-material';
import WorkflowSessionModal from '../../ItemProperties/WorkflowSessionModal';
import MqSeriesMessageHeaders from './MQSeriesCustomComponents/MqSeriesMessageHeaders';
import MqSeriesMessageSelector from './MQSeriesCustomComponents/MqSeriesMessageSelector';
import { useSelector } from 'react-redux';

const MqSeries = ({ connectionDetails, handleBodyContent, direction, screen = '', disableControl = false, repoPDC = {}, useConnection = 'current', source = "" }) => {
    const shouldEncryptDecryptPassword = String(source || "").toLowerCase() !== "pdc";
    const { t } = useTranslation();
    const [queueManagerName, setQueueManagerName] = useState('');
    const [destinationType, setDestinationType] = useState('QUEUE');
    const [destinationName, setDestinationName] = useState('');
    const [durableClientId, setDurableClientId] = useState('');
    const [hostName, setHostname] = useState('');
    const [port, setPort] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [channel, setChannel] = useState('');
    const [timeout, setTimeout] = useState('');
    const [commit, setCommit] = useState('AUTO_COMMIT');
    const [ssl, setSsl] = useState(false);
    const [cipherSuite, setCipherSuite] = useState('');
    const [runOnPrimary, setRunOnPrimary] = useState(false);
    const [content, setContent] = useState(null);
    const [valueLabel, setValueLabel] = useState("");
    const [workflowSessionModal, setWorkflowSessionModal] = useState({
        open: false,
        isFirstTime: false,
    });
    const [headerFieldsDrawerOpen, setHeaderFieldsDrawerOpen] = useState(false);
    const [headerFields, setHeaderFields] = useState({});
    const [customHeaderFields, setCustomHeaderFields] = useState('');
    const [messageSelectorDrawerOpen, setMessageSelectorDrawerOpen] = useState(false);
    const { rightTree } = useSelector((state) => state.treeData[state.treeData.currentScreen]);
    const currentTab = rightTree.currentTab;
    const selectedNode = rightTree.tree[currentTab].selectedNode;

    const workflowMappings = {
        [t('LBL_QUEUE_MANAGER_NAME')]: {
            stateSetter: setQueueManagerName,
            key: "CONNECTION_FACTORY_LOOKUP",
            value: queueManagerName,
        },
        [t("LBL_DESTINATION_NAME")]: {
            stateSetter: setDestinationName,
            key: "QUEUE_OR_TOPIC_NAME",
            value: destinationName,
        },
        [t("LBL_DURABLE_CLIENT_ID")]: {
            stateSetter: setDurableClientId,
            key: "DURABLE_SUBSCRIPTION_CLIENT_ID",
            value: durableClientId,
        },
        [t("LBL_HOST_NAME")]: {
            stateSetter: setHostname,
            key: "HOST_NAME",
            value: hostName,
        },
        [t("LBL_PORT")]: {
            stateSetter: setPort,
            key: "PORT",
            value: port,
        },
        [t("LBL_USERNAME")]: {
            stateSetter: setUsername,
            key: "USERNAME",
            value: username,
        },
        [t("LBL_WS_PASSWORD")]: {
            stateSetter: setPassword,
            key: "PASSWORD",
            value: password,
        },
        [t("LBL_CHANNEL")]: {
            stateSetter: setChannel,
            key: "CHANNEL",
            value: channel,
        },
    };

    const valuesHandler = (value) => {
        if (workflowMappings[valueLabel]) {
            const { stateSetter, key } = workflowMappings[valueLabel];
            if (key === 'PASSWORD') {
                const encryptedValue = shouldEncryptDecryptPassword
                    ? encryptString(value, process.env.REACT_APP_SECRET_KEY2)
                    : value;
                stateSetter(encryptedValue);
                onInputChange(key, encryptedValue);
            } else {
                stateSetter(value);
                onInputChange(key, value);
            }
        }
    };

    const getFontWeight = (control, currentValue, useConnection) => {
        if (!repoPDC) return "normal";
        return repoPDC?.[control] !== currentValue &&
            useConnection === "preDefined"
            ? "bold"
            : "normal";
    };

    const fontWeights = useMemo(() => {
        return {
            queueManagerName: getFontWeight("CONNECTION_FACTORY_LOOKUP", queueManagerName, useConnection),
            destinationType: getFontWeight("DESTINATION_TYPE", destinationType, useConnection),
            destinationName: getFontWeight("QUEUE_OR_TOPIC_NAME", destinationName, useConnection),
            durableClientId: getFontWeight("DURABLE_SUBSCRIPTION_CLIENT_ID", durableClientId, useConnection),
            hostName: getFontWeight("HOST_NAME", hostName, useConnection),
            port: getFontWeight("PORT", port, useConnection),
            username: getFontWeight("USERNAME", username, useConnection),
            password: getFontWeight("PASSWORD", password, useConnection),
            channel: getFontWeight("CHANNEL", channel === "SYSTEM.DEF.SVRCONN" ? undefined : channel, useConnection),
            timeout: getFontWeight("TIMEOUT", timeout, useConnection),
            commit: getFontWeight(
                "TRANSACTIONAL",
                commit === "TRANSACTIONAL_COMMIT" ? "TRUE" : "FALSE",
                useConnection
            ),
            cipher: getFontWeight(
                "CIPHER",
                [t("SELECT"), "-- Select --", "SELECT"].includes(cipherSuite) ? "" : cipherSuite,
                useConnection
            ),
        };
    }, [queueManagerName, destinationType, destinationName, durableClientId, hostName, port, username, password, channel, timeout, commit, cipherSuite, useConnection, repoPDC]);

    const destinationTypeDropDownItems = destinationTypeOptions.map((obj, key) => (
        <MenuItem key={key} value={obj.value}>
            {t(obj.text)}
        </MenuItem>
    ));

    const cipherSuiteDropDownItems = cipherSuiteOptions.map((obj, key) => (
        <MenuItem key={key} value={obj.value}>
            {t(obj.text)}
        </MenuItem>
    ));

    const debouncedContentUpdate = debounce(handleBodyContent, 500);

    const onInputChange = (name, value) => {
        if(['GET_DATA', 'SEND_DATA'].includes(screen)){
            setContent((prev) => ({ add:{ ...prev.add,  [name]: value} }));
            return;
        }
        setContent((prev) => {
            return { ...prev, [name]: value };
        });
    }

    useEffect(() => {
        if(!_.isEqual(content, connectionDetails)) debouncedContentUpdate(content);
    }, [content]);

    useEffect(() => {
        setQueueManagerName(connectionDetails?.CONNECTION_FACTORY_LOOKUP || '');
        setDestinationType(connectionDetails?.DESTINATION_TYPE || 'QUEUE');
        setDestinationName(connectionDetails?.QUEUE_OR_TOPIC_NAME || '');
        setDurableClientId(connectionDetails?.DURABLE_SUBSCRIPTION_CLIENT_ID || '');
        setHostname(connectionDetails?.HOST_NAME || '');
        if (direction === 'IN') setPort(connectionDetails?.PORT || '');
        if (direction === 'OUT') setPort(connectionDetails?.PORT || '');
        setUsername(connectionDetails?.USERNAME || '');
        setPassword(
            connectionDetails?.PASSWORD
                ? shouldEncryptDecryptPassword
                    ? decryptString(connectionDetails?.PASSWORD, process.env.REACT_APP_SECRET_KEY2)
                    : connectionDetails?.PASSWORD
                : ''
        );
        setChannel(connectionDetails?.CHANNEL || 'SYSTEM.DEF.SVRCONN');
        setTimeout(connectionDetails?.TIMEOUT || '');
        if (stringToBooleanConversion(connectionDetails?.TRANSACTIONAL)) setCommit('TRANSACTIONAL_COMMIT');
        else setCommit('AUTO_COMMIT');
        setSsl(stringToBooleanConversion(connectionDetails?.SSL));
        setCipherSuite(connectionDetails?.CIPHER || '-- Select --');
        setRunOnPrimary(stringToBooleanConversion(connectionDetails?.RUN_ON_PRIMARY));
        setContent({ ...connectionDetails })
        // setHeaderFields({
        //     CCSID: connectionDetails?.CCSID || null,
        //     MQ_ENCODING: connectionDetails?.MQ_ENCODING || null,
        //     EXPIRY: connectionDetails?.EXPIRY || null,
        //     PERSISTENCE: connectionDetails?.PERSISTENCE || null,
        //     PRIORITY: connectionDetails?.PRIORITY || null,
        //     TARGET_CLIENT: connectionDetails?.TARGET_CLIENT || null,
        //     VERSION: connectionDetails?.VERSION || null,
        //     REPORT: connectionDetails?.REPORT || null,
        //     MESSAGE_TYPE: connectionDetails?.MESSAGE_TYPE || null,
        //     FEEDBACK: connectionDetails?.FEEDBACK || null,
        //     FORMAT: connectionDetails?.FORMAT || null,
        //     BACKOUT_COUNT: connectionDetails?.BACKOUT_COUNT || null,
        //     ORIGINAL_LENGTH: connectionDetails?.ORIGINAL_LENGTH || null,
        //     CORRELATION_ID: connectionDetails?.CORRELATION_ID || null,
        //     CORRELATION_ID_HEX: connectionDetails?.CORRELATION_ID_HEX || false,
        //     MESSAGE_ID: connectionDetails?.MESSAGE_ID || null,
        //     MESSAGE_ID_HEX: connectionDetails?.MESSAGE_ID_HEX || false,
        //     REPLYTO_QUEUENAME: connectionDetails?.REPLYTO_QUEUENAME || null,
        //     REPLYTO_QUEUEMANAGERNAME: connectionDetails?.REPLYTO_QUEUEMANAGERNAME || null,
        //     USER_ID: connectionDetails?.USER_ID || null,
        //     ACCOUNTING_TOKEN: connectionDetails?.ACCOUNTING_TOKEN || null,
        //     APPLICATION_ID_DATA: connectionDetails?.APPLICATION_ID_DATA || null,
        //     PUT_APPLICATION_NAME: connectionDetails?.PUT_APPLICATION_NAME || null,
        //     PUT_DATE_TIME: connectionDetails?.PUT_DATE_TIME || null,
        //     APPLICATION_ORIGIN_DATA: connectionDetails?.APPLICATION_ORIGIN_DATA || null,
        //     GROUP_ID: connectionDetails?.GROUP_ID || null,
        //     MESSAGE_SEQ_NUM: connectionDetails?.MESSAGE_SEQ_NUM || null,
        //     OFFSET: connectionDetails?.OFFSET || null,
        //     MESSAGE_FLAGS: connectionDetails?.MESSAGE_FLAGS || null,
        // });
        // setCustomHeaderFields(connectionDetails?.APP_DEFINED_HEADER_FIELDS || '');
    }, [connectionDetails]);

    const handleHeaderFieldsClick = () => {
        // Fetch data from selectedNode.OUTBOUND instead of connectionDetails
        const outboundData = selectedNode?.OUTBOUND || {};
        setHeaderFields({
            CCSID: outboundData?.CCSID || null,
            MQ_ENCODING: outboundData?.MQ_ENCODING || null,
            EXPIRY: outboundData?.EXPIRY || null,
            PERSISTENCE: outboundData?.PERSISTENCE || null,
            PRIORITY: outboundData?.PRIORITY || null,
            TARGET_CLIENT: outboundData?.TARGET_CLIENT || null,
            VERSION: outboundData?.VERSION || null,
            REPORT: outboundData?.REPORT || null,
            MESSAGE_TYPE: outboundData?.MESSAGE_TYPE || null,
            FEEDBACK: outboundData?.FEEDBACK || null,
            FORMAT: outboundData?.FORMAT || null,
            BACKOUT_COUNT: outboundData?.BACKOUT_COUNT || null,
            ORIGINAL_LENGTH: outboundData?.ORIGINAL_LENGTH || null,
            CORRELATION_ID: outboundData?.CORRELATION_ID || null,
            CORRELATION_ID_HEX: outboundData?.CORRELATION_ID_HEX || false,
            MESSAGE_ID: outboundData?.MESSAGE_ID || null,
            MESSAGE_ID_HEX: outboundData?.MESSAGE_ID_HEX || false,
            REPLYTO_QUEUENAME: outboundData?.REPLYTO_QUEUENAME || null,
            REPLYTO_QUEUEMANAGERNAME: outboundData?.REPLYTO_QUEUEMANAGERNAME || null,
            USER_ID: outboundData?.USER_ID || null,
            ACCOUNTING_TOKEN: outboundData?.ACCOUNTING_TOKEN || null,
            APPLICATION_ID_DATA: outboundData?.APPLICATION_ID_DATA || null,
            PUT_APPLICATION_NAME: outboundData?.PUT_APPLICATION_NAME || null,
            PUT_DATE_TIME: outboundData?.PUT_DATE_TIME || null,
            APPLICATION_ORIGIN_DATA: outboundData?.APPLICATION_ORIGIN_DATA || null,
            GROUP_ID: outboundData?.GROUP_ID || null,
            MESSAGE_SEQ_NUM: outboundData?.MESSAGE_SEQ_NUM || null,
            OFFSET: outboundData?.OFFSET || null,
            MESSAGE_FLAGS: outboundData?.MESSAGE_FLAGS || null,
        });
        setCustomHeaderFields(outboundData?.APP_DEFINED_HEADER_FIELDS || '');
        setHeaderFieldsDrawerOpen(true);
    };

    return (
        <>
            <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t('LBL_QUEUE_MANAGER_NAME')}
                        type="text"
                        value={queueManagerName || ''}
                        disabled={disableControl || false}
                        inputProps={{
                            style: { fontWeight: fontWeights.queueManagerName }
                        }}
                        onChange={(e) => {
                            onInputChange(e.target.name, e.target.value);
                            setQueueManagerName(e.target.value);
                        }}
                        className={"width-100"}
                        name={"CONNECTION_FACTORY_LOOKUP"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl || false}
                        label={t('LBL_QUEUE_MANAGER_NAME')}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            <FormControl variant="outlined" className="width-100" size="small">
                <InputLabel id="destinationTypeDropdownLabel">
                    {t("LBL_DESTINATION_TYPE")}
                </InputLabel>
                <Select
                    className={"width-100"}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 300,
                            },
                        },
                    }}
                    name={"DESTINATION_TYPE"}
                    labelId="destinationTypeDropdownLabel"
                    label={t("LBL_DESTINATION_TYPE")}
                    id="destinationTypeDropdown"
                    value={destinationType || ''}
                    disabled={disableControl || false}
                    onChange={(e) => {
                        const value = e.target.value;
                        onInputChange(e.target.name, value);
                        setDestinationType(value)
                        if (value === 'QUEUE') {
                            onInputChange('DURABLE_SUBSCRIPTION_CLIENT_ID', null);
                            setDurableClientId('')
                        }
                    }}
                    sx={{ "& .MuiSelect-select": { fontWeight: fontWeights.destinationType } }}
                >
                    {destinationTypeDropDownItems}
                </Select>
            </FormControl>
            <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_DESTINATION_NAME")}
                        type="text"
                        value={destinationName || ''}
                        disabled={disableControl || false}
                        inputProps={{
                            style: { fontWeight: fontWeights.destinationName }
                        }}
                        onChange={(e) => {
                            onInputChange(e.target.name, e.target.value);
                            setDestinationName(e.target.value);
                        }}
                        className={"width-100"}
                        name={"QUEUE_OR_TOPIC_NAME"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl || false}
                        label={t("LBL_DESTINATION_NAME")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            {destinationType === 'TOPIC' && (
                <div style={{ display: "flex", gap: "4px" }}>
                    <FormControl className="width-100" size="small">
                        <TextField
                            variant="outlined"
                            size="small"
                            label={t("LBL_DURABLE_CLIENT_ID")}
                            type="text"
                            value={durableClientId || ''}
                            disabled={disableControl || false}
                            inputProps={{
                                style: { fontWeight: fontWeights.durableClientId }
                            }}
                            onChange={(e) => {
                                onInputChange(e.target.name, e.target.value);
                                setDurableClientId(e.target.value);
                            }}
                            className={"width-100"}
                            name={"DURABLE_SUBSCRIPTION_CLIENT_ID"}
                            autoComplete="off"
                        />
                    </FormControl>
                    {["GET_DATA", "SEND_DATA"].includes(screen) && (
                        <WorkflowSessionModalButton
                            disableControl={disableControl || false}
                            label={t("LBL_DURABLE_CLIENT_ID")}
                            setWorkflowSessionModal={setWorkflowSessionModal}
                            setValueLabel={setValueLabel}
                        />
                    )}
                </div>
            )}
            <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_HOST_NAME")}
                        type="text"
                        value={hostName || ''}
                        disabled={disableControl || false}
                        inputProps={{
                            style: { fontWeight: fontWeights.hostName }
                        }}
                        onChange={(e) => {
                            onInputChange(e.target.name, e.target.value);
                            setHostname(e.target.value);
                        }}
                        className={"width-100"}
                        name={"HOST_NAME"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl || false}
                        label={t("LBL_HOST_NAME")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_PORT")}
                        type="text"
                        value={port}
                        disabled={disableControl || false}
                        inputProps={{
                            style: { fontWeight: fontWeights.port }
                        }}
                        onChange={(e) => {
                            onInputChange(e.target.name, e.target.value);
                            setPort(e.target.value);
                        }}
                        className={"width-100"}
                        name={"PORT"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl || false}
                        label={t("LBL_PORT")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_USERNAME")}
                        type="text"
                        value={username || ''}
                        disabled={disableControl || false}
                        inputProps={{
                            style: { fontWeight: fontWeights.username }
                        }}
                        onChange={(e) => {
                            onInputChange('USERNAME', e.target.value);
                            setUsername(e.target.value);
                        }}
                        className={"width-100"}
                        name={"USER_NAME"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl || false}
                        label={t("LBL_USERNAME")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
                <SmartPasswordField
                    variant="outlined"
                    size="small"
                    label={t("LBL_WS_PASSWORD")}
                    value={password || ''}
                    disabled={disableControl || false}
                    inputProps={{
                        style: { fontWeight: fontWeights.password }
                    }}
                    onChange={(e) => {
                        onInputChange(
                            'PASSWORD',
                            shouldEncryptDecryptPassword
                                ? encryptString(e.target.value, process.env.REACT_APP_SECRET_KEY2)
                                : e.target.value
                        );
                        setPassword(e.target.value);
                    }}
                    className={"width-100"}
                    name={"PASS_WORD"}
                    autoComplete="off"
                />
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl || false}
                        label={t("LBL_WS_PASSWORD")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_CHANNEL")}
                        type="text"
                        value={channel}
                        disabled={disableControl || false}
                        inputProps={{
                            style: { fontWeight: fontWeights.channel }
                        }}
                        onChange={(e) => {
                            onInputChange(e.target.name, e.target.value);
                            setChannel(e.target.value);
                        }}
                        className={"width-100"}
                        name={"CHANNEL"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl || false}
                        label={t("LBL_CHANNEL")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            {screen === 'GET_DATA' && connectionDetails?.ADAPTER_TYPE !== 'BridgeGateMQSeries' && (
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("TIMEOUT")}
                        type="text"
                        value={timeout}
                        disabled={disableControl || false}
                        inputProps={{
                            style: { fontWeight: fontWeights.timeout },
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                        }}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numeric input
                            if (/^\d*$/.test(value)) {
                                onInputChange(e.target.name, value);
                                setTimeout(value);
                            }
                        }}
                        className={"width-100"}
                        name={"TIMEOUT"}
                        autoComplete="off"
                    />
                </FormControl>
            )}
            <div className='flex'>
                <FormControl >
                    <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="TRANSACTIONAL"
                        className="flex justify-center"
                        value={commit || ''}
                        onChange={(e) => {
                            if (e.target.value === 'TRANSACTIONAL_COMMIT') {
                                onInputChange(e.target.name, 'TRUE');
                            } else onInputChange(e.target.name, 'FALSE');
                            setCommit(e.target.value);
                        }}
                    >
                        <FormControlLabel
                            value="AUTO_COMMIT"
                            control={<Radio disabled={disableControl || false} />}
                            label={t('AUTO_COMMIT')}
                            sx={{ "& .MuiFormControlLabel-label": { fontWeight: fontWeights.commit } }}
                        />
                        <FormControlLabel
                            value="TRANSACTIONAL_COMMIT"
                            control={<Radio disabled={disableControl || false} />}
                            label={t('LBL_TRANSACTIONAL_COMMIT')}
                            sx={{ "& .MuiFormControlLabel-label": { fontWeight: fontWeights.commit } }}
                        />
                    </RadioGroup>
                </FormControl>
            </div>
            <div>
                <TPCheckbox
                    label={t("BTN_SSL")}
                    checked={ssl}
                    disabled={disableControl || false}
                    handleCheck={(checked) => {
                        onInputChange('SSL', stringifyJSONAttributes(checked));
                        setSsl(checked);
                        if (!checked) {
                            onInputChange('CIPHER', '')
                            setCipherSuite('-- Select --');
                        }
                    }}
                />
            </div>
            {ssl && <FormControl variant="outlined" className="width-100" size="small">
                <InputLabel id="cipherSuiteDropdownLabel">
                    {t("LBL_CIPHER_SUITE")}
                </InputLabel>
                <Select
                    className={"width-100"}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 300,
                            },
                        },
                    }}
                    name={"CIPHER"}
                    labelId="cipherSuiteDropdownLabel"
                    label={t("LBL_CIPHER_SUITE")}
                    id="cipherSuiteDropdown"
                    value={cipherSuite}
                    disabled={disableControl || false}
                    onChange={(e) => {
                        if (e.target.value !== '-- Select --') onInputChange(e.target.name, e.target.value)
                        setCipherSuite(e.target.value);
                    }}
                    sx={{ "& .MuiSelect-select": { fontWeight: fontWeights.cipher } }}
                >
                    <MenuItem value="-- Select --">
                        -- Select --
                    </MenuItem>
                    {cipherSuiteDropDownItems}
                </Select>
            </FormControl>}

            {connectionDetails?.ADAPTER_TYPE && connectionDetails?.ADAPTER_TYPE !== 'MQ Series' && <div>
                <TPCheckbox
                    label={t("LBL_RUN_ON_PRIMARY")}
                    checked={runOnPrimary}
                    disabled={disableControl || false}
                    handleCheck={(checked) => {
                        onInputChange('RUN_ON_PRIMARY', stringifyJSONAttributes(checked));
                        setRunOnPrimary(checked);
                    }}
                />
            </div>}

            {screen === 'SEND_DATA' && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleHeaderFieldsClick}
                    sx={{ width: '100%', mt: 2 }}
                >
                    {t('LBL_MQ_HEADER_FIELDS')}
                </Button>
            )}
            {screen === 'GET_DATA' && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setMessageSelectorDrawerOpen(true)}
                    sx={{ width: '100%', mt: 2 }}
                >
                    {t('LBL_MESSAGE_SELECTOR')}
                </Button>
            )}
            {headerFieldsDrawerOpen && <MqSeriesMessageHeaders
                open={headerFieldsDrawerOpen}
                onClose={() => setHeaderFieldsDrawerOpen(false)}
                headerFields={headerFields}
                customHeaders={customHeaderFields}
                onSave={(fields, correlationIdHex, messageIdHex, customHeaderFieldsValue) => {
                    setHeaderFieldsDrawerOpen(false);
                    setContent((prev) => ({ add: {
                        ...prev.add,
                        ...fields,
                        CORRELATION_ID_HEX: correlationIdHex ? 'true' : null,
                        MESSAGE_ID_HEX: messageIdHex ? 'true' : null,
                        APP_DEFINED_HEADER_FIELDS: customHeaderFieldsValue || null,
                    }}))
                }}
            />}
            {messageSelectorDrawerOpen && <MqSeriesMessageSelector
                open={messageSelectorDrawerOpen}
                onClose={() => setMessageSelectorDrawerOpen(false)}
            />}

            <WorkflowSessionModal
                valueLabel={valueLabel}
                showAccountData={true}
                showCallingWorkflow={true}
                showSystemData={true}
                showWorkflowSession={true}
                showDateAndTime={true}
                openModal={workflowSessionModal}
                setModalOpen={setWorkflowSessionModal}
                handleClose={() =>
                    setWorkflowSessionModal({ open: false, isFirstTime: false })
                }
                initialValue={workflowMappings[valueLabel]?.value}
                inputType={[t('LBL_WS_PASSWORD')].includes(valueLabel) ? "password" : "text"}
                specialPasswordField={[t('LBL_WS_PASSWORD')].includes(valueLabel)}
                valuesHandler={(value) => {
                    valuesHandler(value);
                }}
            />
        </>
    )
}

export default MqSeries

const WorkflowSessionModalButton = ({setWorkflowSessionModal, label, disableControl, setValueLabel}) => {
    return (
        <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
                setValueLabel(label);
                setWorkflowSessionModal({ open: true, isFirstTime: true });
            }}
            sx={{ width: "40px", minWidth: "40px" }}>
            <MoreHorizOutlined fontSize="small" />
        </Button>
    );
};